import { ErrorCodes, TranscriptionService } from '@klangio/ai2notes-ts-client';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import RetryIcon from '@mui/icons-material/Replay';
import ReplayIcon from '@mui/icons-material/Replay';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useContext } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ExportTranscriptionContext, UserContext } from '../../../../context';
import { TranscriptionStoreContext } from '../../../../context/TranscriptionStoreContext/TranscriptionStoreContext';
import type { ExportType } from '../../../../entities';
import { TranscriptionState } from '../../../../entities';
import { TrackingAction, TrackingCategory, trackingService } from '../../../../services';
import DropdownButton from '../../../molecules/DownloadMenu/DropdownButton';
import DropDownContent from '../../../molecules/DownloadMenu/DropDownContent';
import { safeJSONParse } from '../../../../utils';

export interface ActionButtonConfig {
  key: string;
  condition: () => boolean;
  render?: () => React.ReactNode;
  iconType?: React.ElementType;
  label?: string;
  id?: string;
  disabled?: boolean;
  onClick?: () => void;
}

export enum DialogTypes {
  Rename = 'rename',
  Delete = 'delete',
  Refund = 'refund',
  Retranscribe = 'retranscribe',
  Retry = 'retry',
}

export const useActionsConfig = ({
  handleUnlockTranscription,
  setOpenDialogType,
}: {
  handleUnlockTranscription: (option: ExportType) => void;
  setOpenDialogType: (dialogType: DialogTypes, open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { hasTicketsForUse } = useContext(UserContext);
  const { onDownload } = useContext(ExportTranscriptionContext);
  const threeMinutesAgo = new Date(Date.now() - 3 * 60 * 1000);
  // eslint-disable-next-line max-len
  const {
    selectedTranscription,
    updateTranscriptions,
    transcriptions,
    setTranscriptions,
    setSelectedTranscription,
  } = useContext(TranscriptionStoreContext);

  const handleDownload = (option: ExportType) => {
    trackingService.valueObjectEvent(TrackingCategory.Transcription, TrackingAction.EXPORT_CLICK, {
      // @ts-ignore
      export_type: option,
      location: 'songbook',
      ui_element: 'download',
      pricingModal: selectedTranscription?.demo && !hasTicketsForUse,
    });
    trackingService.event(TrackingCategory.Transcription, TrackingAction.SB_ITEM_CLICK_EXPORT);
    onDownload(selectedTranscription, option);
  };

  const onDelete = useCallback(() => {
    trackingService.event(TrackingCategory.Transcription, TrackingAction.SB_ITEM_CLICK_DELETE);
    TranscriptionService.transcriptionDelete(selectedTranscription.id)
      .then(() => {
        // find transcription index before deleted transcription
        const indexOfDeletedTranscription = transcriptions.findIndex(
          (transcription) => transcription.id === selectedTranscription.id
        );
        const newTranscriptions = cloneDeep(transcriptions);
        newTranscriptions.splice(indexOfDeletedTranscription, 1);
        setTranscriptions(newTranscriptions);
        setSelectedTranscription(newTranscriptions[indexOfDeletedTranscription] ?? null);
        console.log('set deleted');
      })
      .catch(() => {
        console.error('Error deleting transcription');
      });
  }, []);

  const onRename = (newTitle: string) => {
    trackingService.event(TrackingCategory.Transcription, TrackingAction.SB_ITEM_CLICK_EDIT);

    const index = transcriptions.findIndex(
      (transcription) => transcription.id === selectedTranscription.id
    );
    let newTranscriptions = cloneDeep(transcriptions);

    // optimistic update
    if (index >= 0) {
      newTranscriptions = [
        ...transcriptions.slice(0, index),
        {
          ...selectedTranscription,
          title: newTitle,
        },
        ...transcriptions.slice(index + 1),
      ];
    }

    setTranscriptions(newTranscriptions);
    setSelectedTranscription({
      ...selectedTranscription,
      title: newTitle,
    });

    TranscriptionService.transcriptionUpdateTranscription({
      id: selectedTranscription.id,
      title: newTitle,
    }).then(
      () => {
        setOpenDialogType(null, null);
      },
      () => {
        updateTranscriptions();
        setOpenDialogType(null, null);
      }
    );
  };

  const onRetrySocial = () => {
    trackingService.event(TrackingCategory.Transcription, TrackingAction.SB_ITEM_CLICK_RETRY);
    // if (selectedTranscription.demo) {
    //   TranscriptionService.transcriptionSocial({
    //     url: selectedTranscription.source,
    //     useTickets: false,
    //     params: selectedTranscription.parameters,
    //   }).then(() => {
    //     setTimeout(() => {
    //       updateTranscriptions();
    //     }, 1000);
    //   });
    // } else {
    TranscriptionService.transcriptionRetranscribeSheet({
      id: selectedTranscription.id,
      parameters: selectedTranscription.parameters,
    }).then(() => {
      setTimeout(() => {
        updateTranscriptions();
      }, 1000);
    });
    // }
  };

  const config = [
    {
      key: 'download',
      condition: () => selectedTranscription?.state === TranscriptionState.Ok,
      render: () => (
        <DropdownButton variant="songbook">
          <DropDownContent
            transcription={selectedTranscription}
            handleUnlockTranscription={handleUnlockTranscription}
            handleDownloadClick={handleDownload}
          />
        </DropdownButton>
      ),
    },
    {
      key: 'rename',
      condition: () => selectedTranscription?.state === TranscriptionState.Ok,
      id: 'rename',
      iconType: EditIcon,
      label: t('home.songbook.rename'),
      onClick: () => setOpenDialogType(DialogTypes.Rename, true),
    },
    {
      key: 'retranscribe',
      condition: () =>
        selectedTranscription?.state === TranscriptionState.Ok &&
        !selectedTranscription?.demo &&
        safeJSONParse(selectedTranscription?.parameters),
      id: 'retranscribe',
      iconType: ReplayIcon,
      label: t('home.songbook.retranscribe'),
      onClick: () => setOpenDialogType(DialogTypes.Retranscribe, true),
    },
    {
      key: 'retry_yt',
      condition: () =>
        [ErrorCodes._2001, ErrorCodes._2002, ErrorCodes._2003].includes(
          selectedTranscription.error
        ) &&
        (selectedTranscription.state === TranscriptionState.Error ||
          selectedTranscription.state === TranscriptionState.Refunded),
      id: 'retry_yt',
      disabled:
        selectedTranscription.creationDate &&
        new Date(selectedTranscription.creationDate).getTime() > threeMinutesAgo.getTime(),
      iconType: RetryIcon,
      label: t('home.songbook.retry'),
      onClick: () => {
        onRetrySocial();
      },
    },
    {
      key: 'retry_params_error',
      condition: () =>
        [ErrorCodes._1001, ErrorCodes._0].includes(selectedTranscription.error) &&
        safeJSONParse(selectedTranscription?.parameters) &&
        (selectedTranscription.state === TranscriptionState.Error ||
          selectedTranscription.state === TranscriptionState.Refunded),
      id: 'retry_params_error',
      iconType: RetryIcon,
      label: t('home.songbook.retry'),
      onClick: () => setOpenDialogType(DialogTypes.Retranscribe, true),
    },
    {
      key: 'refund',
      condition: () =>
        selectedTranscription?.refundable &&
        selectedTranscription?.state !== TranscriptionState.Enqueued &&
        selectedTranscription?.state !== TranscriptionState.Processing,
      id: 'refund',
      iconType: MoneyOffIcon,
      label: t('home.songbook.refund'),
      onClick: () => {
        trackingService.event(TrackingCategory.Transcription, TrackingAction.SB_ITEM_CLICK_REFUND);
        setOpenDialogType(DialogTypes.Refund, true);
      },
    },
    {
      key: 'delete',
      condition: () =>
        selectedTranscription?.state !== TranscriptionState.Enqueued &&
        selectedTranscription?.state !== TranscriptionState.Processing,
      id: 'delete',
      iconType: DeleteIcon,
      label: t('home.songbook.delete'),
      onClick: () => setOpenDialogType(DialogTypes.Delete, true),
    },
  ];
  return { config: config as ActionButtonConfig[], onRename, onDelete };
};
